import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';

const BlogContainer = styled(motion.div)`
  padding: 120px 20px 80px;
  max-width: 1200px;
  margin: 0 auto;
`;

const Title = styled.h1`
  text-align: center;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 15px;
  font-size: 2.8rem;
  font-weight: 600;

  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
  
  span {
    display: block;
    font-size: 1.2rem;
    color: ${props => props.theme.colors.secondary};
    margin-top: 10px;
    font-weight: normal;
    word-break: keep-all;
    
    @media (max-width: 768px) {
      font-size: 1.1rem;
      line-height: 1.4;
      padding: 0 10px;
      word-spacing: -1px;
    }
    
    @media (max-width: 480px) {
      font-size: 1.05rem;
      word-spacing: -1.5px;
      letter-spacing: -0.3px;
    }
    
    @media (max-width: 400px) {
      font-size: 1rem;
      word-spacing: -2px;
      letter-spacing: -0.5px;
    }
  }
`;

const Description = styled.p`
  text-align: center;
  color: ${props => props.theme.colors.text};
  margin-bottom: 60px;
  line-height: 1.8;
  font-size: 1.1rem;
`;

const BlogGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 30px;
  
  @media (max-width: ${props => props.theme.breakpoints.desktop}) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
  }
`;

const BlogPost = styled.a`
  display: block;
  text-decoration: none;
  color: inherit;
  background: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease;

  &:hover {
    transform: translateY(-5px);
  }
`;

const BlogImage = styled.div`
  width: 100%;
  height: 200px;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const BlogContent = styled.div`
  padding: 20px;

  h2 {
    color: ${props => props.theme.colors.primary};
    margin-bottom: 10px;
    font-size: 1.4rem;
  }

  p {
    color: ${props => props.theme.colors.text};
    line-height: 1.6;
  }
`;

const Blog = () => {
  const blogPosts = [
    {
      id: 1,
      title: "소아진료를 위한 준비사항",
      description: "아이와 함께 치과 방문 시 필요한 준비사항과 주의사항을 알려드립니다. 편안하고 안전한 치과 진료를 위한 팁을 확인하세요.",
      image: "/images/blog/cavity-prevention.jpg",
      link: "https://blog.naver.com/juniordental/221478516047"
    },
    {
      id: 2,
      title: "치아에 흰점과 노란 부분이 생겼어요",
      description: "치아에 생기는 흰 반점과 노란 변색의 원인을 알아보고, 예방과 치료 방법에 대해 알아봅니다.",
      image: "/images/blog/tooth-discoloration.jpg",
      link: "https://blog.naver.com/juniordental/223357646476"
    },
    {
      id: 3,
      title: "불소 도포의 중요성",
      description: "불소 도포는 치아의 에나멜을 강화하고 충치를 예방하는 효과적인 방법입니다. 정기적인 불소 도포로 치아 건강을 지키세요.",
      image: "/images/blog/fluoride-kids-treatment.jpg",
      link: "https://blog.naver.com/juniordental/223309197320"
    }
  ];

  return (
    <BlogContainer
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <Title>
        치아관리정보
        <span>우리 아이 치아 건강을 위한 전문의의 조언</span>
      </Title>
      <Description>
        소아치과 전문의가 전하는 어린이 치아건강 정보와 올바른 관리방법을 알려드립니다.
      </Description>
      <BlogGrid>
        {blogPosts.map(post => (
          <BlogPost key={post.id} href={post.link} target="_blank" rel="noopener noreferrer">
            <BlogImage>
              <img src={post.image} alt={post.title} />
            </BlogImage>
            <BlogContent>
              <h2>{post.title}</h2>
              <p>{post.description}</p>
            </BlogContent>
          </BlogPost>
        ))}
      </BlogGrid>
    </BlogContainer>
  );
};

export default Blog; 